/**
 * TrialSurfaces — the app-level host for every trial popup (TRIAL_PLAN.md §8).
 * Mounted once inside the router so each one can fire from wherever the
 * creator happens to be:
 *   • Step 0 right after signup (§8.3)
 *   • the first-use keep-this-tool prompt (§8.5)
 *   • the final-24h last call (§8.7)
 *   • the lockdown screen after a trial ends (§8.9)
 *
 * Every one of them is self-silencing: each renders nothing unless the
 * backend's trial state says it is due.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getToken, getUser } from "@/lib/api";
import { useTrial } from "@/lib/trial";
import TrialStartModal from "@/components/TrialStartModal";
import TrialKeepToolModal from "@/components/TrialKeepToolModal";
import TrialLastCallModal from "@/components/TrialLastCallModal";
import TrialEndedModal from "@/components/TrialEndedModal";

export const STEP0_FLAG = "mysilo_trial_step0";

// Auth and legal screens are never the right place for a trial popup — a stale
// session on /login would otherwise paint the lockdown modal over the form.
const QUIET_ROUTES = ["/login", "/signup", "/forgot-password", "/reset-password",
                      "/terms", "/privacy", "/refund-policy", "/invite", "/kid",
                      // Oct 2026 (fan-first pass) — /start presents the trial as
                      // one of two real choices and owns its own modal. A popup
                      // over the top of that would be the exact funnel this
                      // page exists to undo.
                      "/start"];

export default function TrialSurfaces() {
  const authed = !!getToken() && !!getUser();
  const { pathname } = useLocation();
  const quiet = QUIET_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const { trial } = useTrial();
  const [step0, setStep0] = useState(false);

  // Step 0 fires once, right after account creation — Signup sets the flag.
  useEffect(() => {
    if (!authed || !trial || quiet) return;
    let pending = false;
    try { pending = sessionStorage.getItem(STEP0_FLAG) === "1"; } catch { /* storage off */ }
    if (!pending) return;
    try { sessionStorage.removeItem(STEP0_FLAG); } catch { /* storage off */ }
    if (trial.eligible && !trial.live && !trial.step0_seen_at) setStep0(true);
  }, [authed, trial, quiet]);

  if (!authed || quiet) return null;

  return (
    <>
      <TrialStartModal open={step0} onOpenChange={setStep0} />
      <TrialKeepToolModal />
      <TrialLastCallModal />
      <TrialEndedModal />
    </>
  );
}
